import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { PhotoUpload } from "@/components/PhotoUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/plant";
import { Sprout, Plus, Trash2, Star, Scale, Loader2 } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["g", "kg", "oz", "lb", "count", "bunch"];

function Stars({ value, onChange, readOnly }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange?.(n)}
          className={readOnly ? "" : "hover:scale-110 transition"}
          data-testid={readOnly ? undefined : `harvest-star-${n}`}
        >
          <Star className={`h-4 w-4 ${n <= value ? "fill-amber-400 text-amber-400" : "text-stone-300 dark:text-stone-600"}`} />
        </button>
      ))}
    </div>
  );
}

export function HarvestSection({ plant, onChanged }) {
  const [harvests, setHarvests] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: "", yield_amount: "", yield_unit: "g", taste_notes: "", rating: 0, photo_path: "" });

  const load = () => api.get(`/plants/${plant.id}/harvests`).then(({ data }) => setHarvests(data)).catch(() => setHarvests([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.id]);

  const openDialog = () => {
    setForm({ date: new Date().toISOString().slice(0, 10), yield_amount: "", yield_unit: "g", taste_notes: "", rating: 0, photo_path: "" });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/plants/${plant.id}/harvests`, {
        date: form.date,
        yield_amount: form.yield_amount === "" ? null : Number(form.yield_amount),
        yield_unit: form.yield_unit,
        taste_notes: form.taste_notes,
        rating: form.rating || null,
        photo_path: form.photo_path,
      });
      toast.success("Harvest logged 🧺");
      setOpen(false);
      load();
      onChanged?.();
    } catch {
      toast.error("Could not save harvest");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/harvests/${id}`);
    load();
  };

  const totalByUnit = harvests.reduce((acc, h) => {
    if (h.yield_amount) acc[h.yield_unit] = (acc[h.yield_unit] || 0) + h.yield_amount;
    return acc;
  }, {});

  return (
    <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm" data-testid="harvest-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <Scale className="h-5 w-5 text-terracotta" style={{ color: "#C25E00" }} /> Harvest Log
          {harvests.length > 0 && <span className="text-sm font-normal text-muted-foreground">({harvests.length})</span>}
        </h2>
        <Button onClick={openDialog} className="bg-orange-600 hover:bg-orange-700 rounded-xl" data-testid="add-harvest-button">
          <Plus className="h-4 w-4 mr-1.5" /> Log harvest
        </Button>
      </div>

      {Object.keys(totalByUnit).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(totalByUnit).map(([unit, amt]) => (
            <span key={unit} className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300">
              Total: {amt} {unit}
            </span>
          ))}
        </div>
      )}

      {harvests.length === 0 ? (
        <p className="text-sm text-muted-foreground">No harvests yet. Log your first yield to compare seasons over time.</p>
      ) : (
        <div className="space-y-3">
          {harvests.map((h) => (
            <div key={h.id} className="flex gap-3 p-3 rounded-xl border border-border group" data-testid="harvest-item">
              {h.photo_path && <AuthImage path={h.photo_path} className="w-16 h-16 rounded-lg object-cover shrink-0" fallbackClassName="w-16 h-16 rounded-lg" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {h.yield_amount != null && <span className="font-semibold text-sm">{h.yield_amount} {h.yield_unit}</span>}
                  {h.rating ? <Stars value={h.rating} readOnly /> : null}
                  <span className="text-xs text-muted-foreground">· {formatDate(h.date)}</span>
                  <button onClick={() => remove(h.id)} className="ml-auto opacity-0 group-hover:opacity-100 transition text-muted-foreground hover:text-destructive" data-testid={`delete-harvest-${h.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {h.taste_notes && <p className="text-sm text-foreground/90 mt-1">{h.taste_notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="harvest-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Sprout className="h-5 w-5 text-orange-600" /> Log a Harvest
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <PhotoUpload value={form.photo_path} onChange={(p) => set("photo_path", p)} testId="harvest-photo" />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="harvest-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Rating</Label>
                <div className="h-10 flex items-center">
                  <Stars value={form.rating} onChange={(n) => set("rating", n)} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Yield</Label>
                <Input type="number" min={0} step="0.1" value={form.yield_amount} onChange={(e) => set("yield_amount", e.target.value)} placeholder="e.g. 250" data-testid="harvest-yield-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Unit</Label>
                <Select value={form.yield_unit} onValueChange={(v) => set("yield_unit", v)}>
                  <SelectTrigger data-testid="harvest-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u} value={u} data-testid={`harvest-unit-${u}`}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Taste notes</Label>
              <Textarea value={form.taste_notes} onChange={(e) => set("taste_notes", e.target.value)} placeholder="Sweet, juicy, better than last season…" rows={3} data-testid="harvest-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="harvest-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="harvest-save-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save harvest
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
